import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Menu, X, Phone, User, LogOut, Mail, MapPin, Clock } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from '../components/ui/button';
import { Logo, LOGO_URL } from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero Section
const HeroSection = ({ settings }) => (
  <section className="relative min-h-screen flex items-center" data-testid="hero-section">
    <div className="absolute inset-0 z-0">
      <img
        src="https://customer-assets-m6fa6gv7.emergentagent.net/job_food-order-hub-89/artifacts/k2vsezk1_PHOTO-2026-09-07-18-29-38.jpg"
        alt="SizzleBox Food"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
    </div>
    
    <div className="container mx-auto px-6 relative z-10">
      <div className="max-w-2xl">
        <p className="text-fire-500 font-accent text-xl mb-4 uppercase tracking-wider">
          Your Cravings, Our Specialty. 🔥
        </p>
        <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl text-white leading-none mb-6">
          Welcome to<br />
          <span className="text-fire-500">Sizzlebox</span>
        </h1>
        <p className="text-gray-300 text-lg md:text-xl mb-4 leading-relaxed">
          Serving up grills, gourmet food and all your favourite comfort foods in Lagos.
        </p>
        <p className="text-gray-400 text-base md:text-lg mb-4 leading-relaxed">
          Think juicy burgers, cheesy pizzas, loaded fries, wraps, delicious grills, creamy milkshakes and refreshing juices — made fresh and packed with flavour.
        </p>
        <p className="text-white font-bold text-lg md:text-xl mb-8">
          Freshly made. Generously served. Seriously delicious.
        </p>
        <div className="flex flex-wrap gap-4 mb-8">
          <a
            href={`https://wa.me/${settings?.whatsapp?.replace(/[^0-9]/g, '')}?text=Hi! I'd like to place an order`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-now-btn"
          >
            <Button className="bg-fire-500 hover:bg-fire-600 text-white rounded-full px-8 py-6 text-lg font-bold shadow-[0_0_30px_rgba(255,69,0,0.4)] hover:shadow-[0_0_40px_rgba(255,69,0,0.6)] transition-all">
              Order Now
            </Button>
          </a>
          <Link to="/menu" data-testid="view-menu-btn">
            <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black rounded-full px-8 py-6 text-lg font-bold transition-all">
              View Menu
            </Button>
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex -space-x-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="w-10 h-10 rounded-full bg-gray-700 border-2 border-black" />
            ))}
          </div>
          <div>
            <p className="text-white font-bold">2,500+</p>
            <p className="text-gray-400 text-sm">Happy Customers</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Categories Section
const CategoriesSection = ({ categories }) => (
  <section className="py-20 bg-black" data-testid="categories-section">
    <div className="container mx-auto px-6">
      <div className="text-center mb-12">
        <p className="text-fire-500 font-accent text-lg mb-2">Browse Our</p>
        <h2 className="font-heading text-4xl md:text-5xl text-white">Menu Categories</h2>
      </div>
      
      <div className="flex flex-wrap justify-center gap-6">
        {categories.map(cat => (
          <Link
            key={cat.id}
            to={`/menu?category=${cat.slug}`}
            className="group flex flex-col items-center p-6 bg-card rounded-2xl border border-white/10 hover:border-fire-500/50 transition-all hover:scale-105"
            data-testid={`category-${cat.slug}`}
          >
            <div className="w-20 h-20 rounded-full overflow-hidden mb-4 border-2 border-white/20 group-hover:border-fire-500 transition-colors">
              {cat.image_url ? (
                <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-fire-500/20 flex items-center justify-center text-2xl">🍽️</div>
              )}
            </div>
            <h3 className="text-white font-bold text-lg">{cat.name}</h3>
            <p className="text-gray-500 text-sm">{cat.description}</p>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// Featured Menu Section
const FeaturedMenuSection = ({ items, onAddToCart, settings }) => (
  <section className="py-20 bg-[#0a0a0a]" data-testid="featured-menu-section">
    <div className="container mx-auto px-6">
      <div className="flex justify-between items-end mb-12">
        <div>
          <p className="text-fire-500 font-accent text-lg mb-2">Popular Picks</p>
          <h2 className="font-heading text-4xl md:text-5xl text-white">Featured Menu</h2>
        </div>
        <Link to="/menu" className="text-white hover:text-fire-500 transition-colors font-semibold flex items-center gap-2">
          View Full Menu <span>→</span>
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.slice(0, 6).map(item => (
          <div
            key={item.id}
            className="group bg-card rounded-2xl overflow-hidden border border-white/10 hover:border-fire-500/50 transition-all"
            data-testid={`menu-item-${item.id}`}
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src={item.image_url}
                alt={item.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              {item.tags?.includes('bestseller') && (
                <span className="absolute top-4 left-4 bg-fire-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  🔥 Bestseller
                </span>
              )}
            </div>
            <div className="p-6">
              <h3 className="text-white font-bold text-xl mb-2">{item.name}</h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-2">{item.description}</p>
              <div className="flex justify-between items-center">
                <span className="text-fire-500 font-bold text-xl">₦{item.price.toLocaleString()}</span>
                <div className="flex gap-2">
                  <a
                    href={`https://wa.me/${settings?.whatsapp?.replace(/[^0-9]/g, '')}?text=Hi! I'd like to order ${item.name} - ₦${item.price.toLocaleString()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#25D366] hover:bg-[#128C7E] text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                    data-testid={`whatsapp-order-${item.id}`}
                  >
                    WhatsApp
                  </a>
                  <Button
                    onClick={() => onAddToCart(item)}
                    className="bg-fire-500 hover:bg-fire-600 text-white px-4 py-2 rounded-full text-sm font-semibold"
                    data-testid={`add-to-cart-${item.id}`}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Gallery Section
const GallerySection = ({ settings }) => {
  const images = settings?.gallery_images?.length > 0 ? settings.gallery_images : [];

  if (images.length === 0) return null;

  return (
    <section id="gallery" className="py-20 bg-black" data-testid="gallery-section">
      <div className="container mx-auto px-6">
        <div className="mb-12">
          <p className="text-fire-500 font-accent text-lg mb-2">Our Gallery</p>
          <h2 className="font-heading text-4xl md:text-5xl text-white heading-line">Gallery</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, i) => (
            <div key={i} className="relative aspect-square rounded-xl overflow-hidden gallery-item group cursor-pointer animate-fade-in" style={{ animationDelay: `${i * 0.08}s` }}>
              <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ settings }) => (
  <section className="py-20 bg-[#0a0a0a] relative overflow-hidden" data-testid="cta-section">
    <div className="container mx-auto px-6 relative z-10">
      <div className="text-center max-w-2xl mx-auto">
        <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">
          Get It While It's Hot! 🔥
        </h2>
        <p className="text-gray-400 mb-8">
          Craving something delicious? Your next favorite meal is just a click away.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href={`https://wa.me/${settings?.whatsapp?.replace(/[^0-9]/g, '')}?text=Hi! I'd like to place an order`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-whatsapp-btn"
          >
            <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full px-8 py-4 font-bold text-lg">
              Order on WhatsApp
            </Button>
          </a>
          <Link to="/menu" data-testid="cta-delivery-btn">
            <Button className="bg-fire-500 hover:bg-fire-600 text-white rounded-full px-8 py-4 font-bold text-lg">
              Order on Delivery App
            </Button>
          </Link>
        </div>
        <p className="text-gray-500 mt-6 flex items-center justify-center gap-2">
          <span className="text-fire-500">🔥</span> Fast delivery across Lagos • Hot meals, always
        </p>
      </div>
    </div>
  </section>
);

// Stats Section
const StatsSection = () => {
  const stats = [
    { icon: "🍔", value: "10,000+", label: "Orders Delivered" },
    { icon: "⭐", value: "4.9/5", label: "Customer Rating" },
    { icon: "🔥", value: "12", label: "Menu Categories" },
    { icon: "⏱️", value: "30-45 min", label: "Average Delivery" },
  ];

  return (
    <section className="py-16 bg-black border-y border-white/10" data-testid="stats-section">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-4xl mb-2">{stat.icon}</div>
              <div className="text-white font-bold text-2xl md:text-3xl">{stat.value}</div>
              <div className="text-gray-500 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    { name: "Tunde A.", location: "VI, Lagos", text: "Best sandwich I've had in Lagos! The Big Pop is a beast 🔥", rating: 5 },
    { name: "Sarah M.", location: "Lekki", text: "Chicken shawarma is mad nice. I order almost every week.", rating: 5 },
    { name: "Kola D.", location: "Yaba", text: "Finally a spot that gets sandwiches right. Fast delivery too!", rating: 5 },
  ];

  return (
    <section className="py-20 bg-[#0a0a0a]" data-testid="testimonials-section">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-fire-500 font-accent text-lg mb-2">What Our Customers Say</p>
          <h2 className="font-heading text-4xl md:text-5xl text-white">Testimonials</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-card p-6 rounded-2xl border border-white/10">
              <p className="text-gray-300 mb-4">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-700" />
                <div>
                  <p className="text-white font-bold">{t.name}</p>
                  <p className="text-gray-500 text-sm">{t.location}</p>
                </div>
                <div className="ml-auto text-fire-orange">
                  {"★".repeat(t.rating)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = ({ settings }) => (
  <section id="about" className="py-20 bg-black" data-testid="about-section">
    <div className="container mx-auto px-6">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-fire-500 font-accent text-lg mb-2">About Us</p>
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">About {settings?.restaurant_name || 'SizzleBox'}</h2>
          <p className="text-gray-400 mb-6 leading-relaxed">
            {settings?.about_text || "SizzleBox is Lagos' go-to spot for bold, flavorful, and seriously satisfying sandwiches, shawarma, and box meals. We don't just make food — we create experiences."}
          </p>
          <p className="text-gray-400 mb-8 leading-relaxed">
            {settings?.about_text_2 || "Every meal is made with premium ingredients, packed with flavor, and delivered hot to your doorstep."}
          </p>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <span className="text-fire-500 text-xl">🔥</span>
              <span className="text-white">Fresh Ingredients</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-fire-500 text-xl">🚀</span>
              <span className="text-white">Fast Delivery</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-fire-500 text-xl">💥</span>
              <span className="text-white">Bold Flavors</span>
            </div>
          </div>
        </div>
        <div className="relative">
          <img
            src={settings?.about_image || "https://images.unsplash.com/photo-1705512557389-841efbde54db?w=600"}
            alt={`About ${settings?.restaurant_name || 'SizzleBox'}`}
            className="rounded-2xl"
          />
        </div>
      </div>
    </div>
  </section>
);

// Contact Section
const ContactSection = ({ settings }) => (
  <section id="contact" className="py-20 bg-[#0a0a0a]" data-testid="contact-section">
    <div className="container mx-auto px-6">
      <div className="text-center mb-12">
        <p className="text-fire-500 font-accent text-lg mb-2">Get In Touch</p>
        <h2 className="font-heading text-4xl md:text-5xl text-white">Contact Us</h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
        <div className="bg-card rounded-2xl border border-white/10 p-8 text-center card-hover animate-fade-in">
          <div className="w-14 h-14 bg-fire-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Phone className="w-6 h-6 text-fire-500" />
          </div>
          <h3 className="text-white font-bold text-lg mb-2">Call Us</h3>
          <p className="text-gray-400">{settings?.phone || '09029809534'}</p>
          <a href={`https://wa.me/${(settings?.whatsapp || '09029809534').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-[#25D366] text-sm mt-2 inline-block hover:underline">
            WhatsApp: {settings?.whatsapp || '09029809534'}
          </a>
        </div>
        
        <div className="bg-card rounded-2xl border border-white/10 p-8 text-center card-hover animate-fade-in" style={{ animationDelay: '0.15s' }}>
          <div className="w-14 h-14 bg-fire-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-6 h-6 text-fire-500" />
          </div>
          <h3 className="text-white font-bold text-lg mb-2">Visit Us</h3>
          <p className="text-gray-400">{settings?.address || 'Lagos, Nigeria'}</p>
          <a href={`mailto:${settings?.email || 'Sizzleboxng@gmail.com'}`} className="text-fire-500 text-sm mt-2 inline-flex items-center gap-1 hover:underline">
            <Mail className="w-3 h-3" /> {settings?.email || 'Sizzleboxng@gmail.com'}
          </a>
        </div>
        
        <div className="bg-card rounded-2xl border border-white/10 p-8 text-center card-hover animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="w-14 h-14 bg-fire-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-6 h-6 text-fire-500" />
          </div>
          <h3 className="text-white font-bold text-lg mb-2">Opening Hours</h3>
          <p className="text-gray-400">{settings?.opening_hours || 'Mon-Sat: 10am - 10pm'}</p>
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = ({ settings }) => (
  <footer className="bg-[#0a0a0a] border-t border-white/10 py-16" data-testid="footer">
    <div className="container mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div>
          <Logo className="h-14 mb-4" />
          <p className="text-gray-400">Your Cravings, Our Specialty. 🔥</p>
          <p className="text-gray-500 text-sm mt-2">Grills, gourmet food and all your favourite comfort foods in Lagos.</p>
        </div>
        
        <div>
          <h4 className="text-white font-bold mb-4">Quick Links</h4>
          <ul className="space-y-2">
            <li><Link to="/" className="text-gray-400 hover:text-fire-500 transition-colors">Home</Link></li>
            <li><Link to="/menu" className="text-gray-400 hover:text-fire-500 transition-colors">Menu</Link></li>
            <li><Link to="/cart" className="text-gray-400 hover:text-fire-500 transition-colors">Cart</Link></li>
            <li><Link to="/account" className="text-gray-400 hover:text-fire-500 transition-colors">My Account</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="text-white font-bold mb-4">Menu</h4>
          <ul className="space-y-2">
            <li><Link to="/menu?category=grill-house" className="text-gray-400 hover:text-fire-500 transition-colors">Grill House</Link></li>
            <li><Link to="/menu?category=burgers-wraps-pizza" className="text-gray-400 hover:text-fire-500 transition-colors">Burgers, Wraps & Pizza</Link></li>
            <li><Link to="/menu?category=sizzle-meals" className="text-gray-400 hover:text-fire-500 transition-colors">Sizzle Meals</Link></li>
            <li><Link to="/menu?category=drinks" className="text-gray-400 hover:text-fire-500 transition-colors">Milkshakes & Drinks</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="text-white font-bold mb-4">Contact</h4>
          <ul className="space-y-3 text-gray-400">
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-fire-500" /> {settings?.phone || '09029809534'}
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-fire-500" /> {settings?.email || 'Sizzleboxng@gmail.com'}
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-fire-500" /> {settings?.address || 'Lagos, Nigeria'}
            </li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-white/10 mt-12 pt-8 text-center text-gray-500">
        <p>&copy; {new Date().getFullYear()} SizzleBox. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Header/Navbar
const Navbar = ({ itemCount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, customer, logout } = useCustomerAuth();
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10" data-testid="navbar">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <Logo className="h-12" />
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-white hover:text-fire-500 transition-colors">Home</Link>
            <Link to="/menu" className="text-white hover:text-fire-500 transition-colors">Menu</Link>
            <a href="#gallery" className="text-white hover:text-fire-500 transition-colors">Gallery</a>
            <a href="#about" className="text-white hover:text-fire-500 transition-colors">About</a>
            <a href="#contact" className="text-white hover:text-fire-500 transition-colors">Contact</a>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/cart" className="relative" data-testid="cart-icon">
              <ShoppingCart className="w-6 h-6 text-white" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-fire-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            
            {isAuthenticated ? (
              <Link to="/account" data-testid="navbar-account-btn">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-4 hidden md:flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {customer?.name?.split(' ')[0] || 'Account'}
                </Button>
              </Link>
            ) : (
              <Link to="/login" data-testid="navbar-login-btn">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-4 hidden md:flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Sign In
                </Button>
              </Link>
            )}
            
            <Link to="/menu" data-testid="navbar-order-btn">
              <Button className="bg-fire-500 hover:bg-fire-600 text-white rounded-full px-6 hidden md:flex">
                Order Now
              </Button>
            </Link>
            <button className="md:hidden text-white" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-white hover:text-fire-500 transition-colors" onClick={() => setIsOpen(false)}>Home</Link>
              <Link to="/menu" className="text-white hover:text-fire-500 transition-colors" onClick={() => setIsOpen(false)}>Menu</Link>
              <Link to="/cart" className="text-white hover:text-fire-500 transition-colors" onClick={() => setIsOpen(false)}>Cart ({itemCount})</Link>
              {isAuthenticated ? (
                <>
                  <Link to="/account" className="text-white hover:text-fire-500 transition-colors flex items-center gap-2" onClick={() => setIsOpen(false)}>
                    <User className="w-4 h-4" /> My Account
                  </Link>
                  <button onClick={() => { logout(); setIsOpen(false); }} className="text-gray-400 hover:text-fire-500 transition-colors flex items-center gap-2 text-left" data-testid="mobile-logout-btn">
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </>
              ) : (
                <Link to="/login" className="text-fire-500 hover:text-fire-400 transition-colors flex items-center gap-2" onClick={() => setIsOpen(false)} data-testid="mobile-login-link">
                  <User className="w-4 h-4" /> Sign In
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Main HomePage Component
export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addItem, itemCount } = useCart();

  useEffect(() => {
    document.title = 'SizzleBox - Grills, Gourmet Food & Comfort Foods in Lagos';
    const fetchData = async () => {
      try {
        const [catRes, menuRes, settingsRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/menu?featured=true`),
          axios.get(`${API}/settings`)
        ]);
        setCategories(catRes.data);
        setMenuItems(menuRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddToCart = (item) => {
    addItem({
      menu_item_id: item.id,
      name: item.name,
      price: item.price,
      image_url: item.image_url
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="home-page">
      <Navbar itemCount={itemCount} />
      <HeroSection settings={settings} />
      <CategoriesSection categories={categories} />
      <FeaturedMenuSection items={menuItems} onAddToCart={handleAddToCart} settings={settings} />
      <GallerySection settings={settings} />
      <CTASection settings={settings} />
      <StatsSection />
      <TestimonialsSection />
      <AboutSection settings={settings} />
      <ContactSection settings={settings} />
      <Footer settings={settings} />
    </div>
  );
}
